import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Inbox, RefreshCw, Search, X } from 'lucide-react';
import apiClient from '../../../utils/apiClient';

const numberFormat = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const numeric = value => value == null || String(value).trim() === '' || !Number.isFinite(Number(value)) ? null : Number(value);
const price = value => numeric(value) == null ? '—' : `$${numberFormat.format(Number(value))}`;
const percentage = value => numeric(value) == null ? '—' : `${Number(value).toFixed(2)}%`;

const CMEOptionScreenerView = () => {
    const [snapshot, setSnapshot] = useState({ data: [], date: null });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [query, setQuery] = useState('');
    const [sort, setSort] = useState({ key: 'symbol', descending: false });
    const request = useRef(null);

    const loadSnapshot = useCallback(async () => {
        request.current?.abort();
        const controller = new AbortController();
        request.current = controller;
        setLoading(true);
        setError('');
        try {
            const { data } = await apiClient.get('/alpaca/cme-straddle-costs', { signal: controller.signal });
            if (!Array.isArray(data?.data)) throw new Error('Invalid CME snapshot');
            if (!controller.signal.aborted) setSnapshot({ data: data.data, date: data.date });
        } catch (err) {
            if (!controller.signal.aborted) setError('Could not load the saved CME snapshot. Please try refreshing.');
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSnapshot();
        return () => request.current?.abort();
    }, [loadSnapshot]);

    const rows = useMemo(() => {
        const search = query.trim().toLowerCase();
        return snapshot.data.filter(row => !search ||
            ['symbol', 'name', 'underlying_symbol', 'option_root', 'ce_symbol', 'pe_symbol']
                .some(key => String(row[key] ?? '').toLowerCase().includes(search))
        ).sort((a, b) => {
            const left = sort.key === 'symbol' ? a.symbol : numeric(a[sort.key]);
            const right = sort.key === 'symbol' ? b.symbol : numeric(b[sort.key]);
            if (left == null && right != null) return 1;
            if (right == null && left != null) return -1;
            const comparison = sort.key === 'symbol' ? String(left).localeCompare(String(right))
                : left == null ? 0 : left - right;
            return (sort.descending ? -comparison : comparison) || a.symbol.localeCompare(b.symbol);
        });
    }, [snapshot.data, query, sort]);

    const changeSort = key => setSort(previous => ({ key, descending: previous.key === key ? !previous.descending : false }));
    const sortHeader = (key, label, alignRight = true) => (
        <th scope="col" className={`p-3 ${alignRight ? 'text-right' : ''}`}
            aria-sort={sort.key === key ? (sort.descending ? 'descending' : 'ascending') : 'none'}>
            <button onClick={() => changeSort(key)} className="hover:text-white whitespace-nowrap">
                {label}{sort.key === key ? (sort.descending ? ' ↓' : ' ↑') : ''}
            </button>
        </th>
    );
    const pricedCount = snapshot.data.filter(row => numeric(row.straddle_cost) != null).length;

    return (
        <section aria-label="CME Screener" className="flex-1 flex flex-col min-h-0 bg-[#14233a] border border-[#1a2b4c] rounded-xl p-4 shadow-lg text-white">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4 border-b border-white/5 pb-3">
                <div className="flex items-center gap-4 flex-wrap">
                    <h2 className="text-xs font-bold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5 rounded-md">
                        CME Screener
                    </h2>
                    <span className="text-[10px] font-bold px-2.5 py-1 rounded-md border border-amber-400/20 text-amber-400 bg-amber-400/10">
                        SAVED SNAPSHOT
                    </span>
                    {snapshot.date && <span className="text-[10px] text-zinc-400 font-mono">Calculation date: {snapshot.date}</span>}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative flex-1 sm:flex-none">
                        <Search aria-hidden="true" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <input aria-label="Search CME symbols" type="text" placeholder="Search CME symbol or name…"
                            value={query} onChange={event => setQuery(event.target.value)}
                            className="bg-[#22272b] border border-transparent focus:border-zinc-500 rounded-lg pl-9 pr-8 py-1.5 text-xs text-[#b6c2cf] focus:outline-none transition-all placeholder:text-zinc-500 w-full sm:w-64 shadow-inner" />
                        {query && <button aria-label="Clear search" onClick={() => setQuery('')}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white">
                            <X className="w-3.5 h-3.5" />
                        </button>}
                    </div>
                    <button onClick={loadSnapshot} disabled={loading} title="Reload the latest saved database snapshot"
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs text-zinc-300 hover:text-white transition-all disabled:opacity-50">
                        <RefreshCw aria-hidden="true" className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                        {loading ? 'Refreshing…' : 'Refresh'}
                    </button>
                </div>
            </div>

            {error && <div role="alert" className="mb-3 px-3 py-2 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-lg">{error}</div>}

            {rows.length > 0 ? (
                <div className="overflow-auto rounded-lg border border-white/5 bg-[#0d0f18]/65 custom-scroll" aria-busy={loading}>
                    <table aria-label="CME ATM straddle costs" className="w-full min-w-[1050px] text-left border-collapse">
                        <thead>
                            <tr className="bg-[#1a2b4c]/60 text-xs font-bold border-b border-white/10 text-center">
                                <th className="p-3 border-r border-white/5" rowSpan={2} scope="col">#</th>
                                <th className="p-3 border-r border-white/5" colSpan={3} scope="colgroup">Asset Underlying Info</th>
                                <th className="p-3" colSpan={4} scope="colgroup">ATM Straddle Options</th>
                            </tr>
                            <tr className="bg-[#14233a] border-b border-white/10 text-zinc-400 text-[11px] font-semibold">
                                {sortHeader('symbol', 'Symbol', false)}
                                {sortHeader('underlying_price', 'Price')}
                                {sortHeader('dte', 'DTE')}
                                <th scope="col" className="p-3">CE / PE Symbol</th>
                                <th scope="col" className="p-3 text-right">Call / Put LTP</th>
                                {sortHeader('straddle_cost', 'Straddle Cost')}
                                {sortHeader('cost_pct', 'Cost %')}
                            </tr>
                        </thead>
                        <tbody className="text-xs">
                            {rows.map((row, index) => (
                                <tr key={row.symbol} className="hover:bg-white/5 transition-colors border-b border-white/5 last:border-b-0">
                                    <td className="p-3 text-center text-zinc-500">{index + 1}</td>
                                    <td className="p-3">
                                        <div className="flex items-center gap-2 text-emerald-400 font-semibold">
                                            {row.name || row.symbol}
                                            <span className="text-[10px] text-zinc-400 font-mono font-normal">{row.symbol}</span>
                                        </div>
                                        <div className="text-[10px] text-zinc-400 font-mono mt-1">{row.underlying_symbol || '—'}</div>
                                    </td>
                                    <td className="p-3 text-right font-semibold whitespace-nowrap tabular-nums">{price(row.underlying_price)}</td>
                                    <td className="p-3 text-right whitespace-nowrap">
                                        <div>{numeric(row.dte) == null ? '—' : `${row.dte}d`}</div>
                                        <div className="text-[10px] text-zinc-400 mt-1" title="Option expiry">{row.expiration_date || '—'}</div>
                                    </td>
                                    <td className="p-3">
                                        <div className="flex items-center gap-1 mb-1 font-mono text-[10px] text-zinc-300">
                                            <span className="text-emerald-400 bg-emerald-400/10 px-1 rounded">C</span>{row.ce_symbol || '—'}
                                        </div>
                                        <div className="flex items-center gap-1 font-mono text-[10px] text-zinc-300">
                                            <span className="text-rose-400 bg-rose-400/10 px-1 rounded">P</span>{row.pe_symbol || '—'}
                                        </div>
                                        <div className="text-[10px] text-zinc-400 mt-1">
                                            {row.option_root} · ATM strike: {price(row.atm_strike)}
                                        </div>
                                    </td>
                                    <td className="p-3 text-right text-zinc-400 whitespace-nowrap tabular-nums">
                                        {price(row.ce_ltp)} <span className="text-zinc-600">/</span> {price(row.pe_ltp)}
                                    </td>
                                    <td className="p-3 text-right font-bold whitespace-nowrap tabular-nums">{price(row.straddle_cost)}</td>
                                    <td className={`p-3 text-right font-bold tabular-nums ${numeric(row.cost_pct) == null ? 'text-zinc-500'
                                        : Number(row.cost_pct) > 15 ? 'text-emerald-400' : Number(row.cost_pct) >= 8 ? 'text-amber-400' : 'text-rose-400'}`}>
                                        {percentage(row.cost_pct)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div role="status" className="flex flex-col items-center justify-center py-20 text-zinc-500">
                    {loading ? <RefreshCw className="w-8 h-8 animate-spin mb-3" /> : <Inbox className="w-12 h-12 text-zinc-600 mb-3" />}
                    <p className="text-sm font-semibold">{loading ? 'Loading CME snapshot…'
                        : error ? 'CME snapshot unavailable' : snapshot.data.length ? 'No symbols match your search.' : 'No saved CME data available.'}</p>
                </div>
            )}
            {snapshot.data.length > 0 && <div className="flex justify-between gap-3 flex-wrap mt-3 text-[10px] text-zinc-400">
                <span>{rows.length} of {snapshot.data.length} products · {pricedCount} priced</span>
                <span>Saved historical LTP · Prices and straddle costs in USD per quoted unit</span>
            </div>}
        </section>
    );
};

export default CMEOptionScreenerView;
